import type { ReactElement } from 'react';
import { CloudDownload } from 'lucide-react';
import { AgentVersionBadge } from './AgentVersionBadge';
import { isAgentVersionOutdated } from './agentVersion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AgentVersionWithUpdateProps {
  version: string | null | undefined;
  /**
   * The real latest GA version (from `useLatestReleases` → `/api/releases/latest`).
   * Freshness is judged against THIS, not the dashboard build's
   * `VITE_VSIX_VERSION`, so a version ahead of GA isn't wrongly flagged.
   */
  latestVersion: string | null | undefined;
  className?: string;
}

/**
 * KIR-2962: an agent version plus an amber "Update available" pill when it's
 * older than the real latest release. Shared by the Inventory → Users DataTable
 * cell and the endpoints dialog so both judge freshness against the same GA
 * source and render the badge identically. Never nags on uncertainty (unknown
 * version or unknown GA → no pill).
 */
export function AgentVersionWithUpdate({
  version,
  latestVersion,
  className,
}: AgentVersionWithUpdateProps): ReactElement {
  const outdated = isAgentVersionOutdated(version, latestVersion);
  return (
    <span className="inline-flex items-center gap-1.5">
      <AgentVersionBadge
        version={version}
        currentVersion={latestVersion}
        className={className}
      />
      {outdated && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <CloudDownload className="h-3 w-3" />
              Update available
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">
            A newer Kirin release is available — the latest is v{latestVersion},
            this is on v{version}.
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

AgentVersionWithUpdate.displayName = 'AgentVersionWithUpdate';
